import React, { type ReactNode, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { type MapDispatchToProps, type MapStateToProps, connect } from 'react-redux';

import { BackstageLoggerDefaultArgs, messages } from '../../constants';
import * as D from '../../deps';

const { AUDIO_PLAY_TRACK, PAUSE } = D.Constants.AudioPlayer.AudioPlayerActions;
const { STATION_TRIGGER } = D.Constants.SourceCard.SourceCardTriggerTypes;
const { ADDTOCOLLECTION, REMOVEFROMCOLLECTION, SHARE } = D.Constants.SourceCard.SourceCardConstants;

export interface IAlbumButtonControlsStateProps {
  showPremiumAccess: ReturnType<typeof D.Utils.Listener.showPremiumAccess>;
  playbackMessages: $TSFixMe;
}

export interface IAlbumButtonControlsDispatchProps {
  playResumeSource: typeof D.playResumeSource;
  pauseTrack: typeof D.pauseTrack;
  addCollectionItem: typeof D.addCollectionItem;
  removeCollectionItem: typeof D.removeCollectionItem;
  openModal: typeof D.openModal;
  closeModal: typeof D.closeModal;
  startArtistStation: typeof D.startStation;
  openShareModal: typeof D.openShareModal;
}

export interface IAlbumButtonControlsOwnProps {
  pandoraId: string;
  pandoraType: keyof typeof D.PandoraTypes;
  shareableUrlPath: string;
  artistPandoraId: string;
  artistName: string;
  artistHasNoRadio: boolean;
}

export type IAlbumButtonControlsProps = IAlbumButtonControlsStateProps &
  IAlbumButtonControlsDispatchProps &
  IAlbumButtonControlsOwnProps;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useClickControls(props: IAlbumButtonControlsProps) {
  const { integration } = useContext(D.AppContext);
  const {
    pandoraId,
    pandoraType,
    shareableUrlPath,
    artistPandoraId,
    artistName,
    artistHasNoRadio,
    showPremiumAccess,
    playbackMessages,
    playResumeSource,
    pauseTrack,
    addCollectionItem,
    removeCollectionItem,
    openModal,
    closeModal,
    startArtistStation,
    openShareModal,
  } = props;

  const logClickEvent = (componentEventType: $TSFixMe): void => {
    integration.action.clicked({
      ...BackstageLoggerDefaultArgs.controlBar,
      ...{ pandoraId, componentEventType },
    });
  };

  const playOnClick = (): void => {
    logClickEvent(AUDIO_PLAY_TRACK);
    // TODO: WEBCA-2934 investigate why we're only passing these props through
    // to PLAY_RESUME_SOURCE.
    // @ts-expect-error
    playResumeSource({
      context: D.Constants.Ads.Rewards.ContextTypes.BACKSTAGE,
      forceRestartSourceIfPlaying: showPremiumAccess,
      id: pandoraId,
      pageUrl: shareableUrlPath,
      pandoraId,
      pandoraType,
    });
  };

  const pauseOnClick = (): void => {
    logClickEvent(PAUSE);
    pauseTrack();
  };

  const removeMusicClick = (): void => {
    logClickEvent(REMOVEFROMCOLLECTION);
    removeCollectionItem({ pandoraId });
  };

  const addToMusicClick = (): void => {
    logClickEvent(ADDTOCOLLECTION);
    addCollectionItem({
      pandoraId,
      pandoraType: undefined,
      stationId: undefined,
    });
  };

  const onModalStartStationClick = (): void => {
    closeModal();
    // TODO: WEBCA-2936 investigate why we're only passing these props through
    // to START_STATION.
    // @ts-expect-error
    startArtistStation({
      pandoraId: artistPandoraId,
      rerouteBasedOnTier: true,
      componentName: 'BackstageAlbum',
    });
  };

  const getStartStationButton = (): ReactNode | null => {
    if (artistHasNoRadio) return null;
    return [
      <D.ButtonT3
        key={artistPandoraId}
        size={D.ButtonT3Size.SMALL}
        onClick={onModalStartStationClick}
      >
        <FormattedMessage {...playbackMessages.startArtistStation} />
      </D.ButtonT3>,
    ];
  };

  const getStartArtistStationMessage = (): ReturnType<typeof FormattedMessage> | null => {
    if (artistHasNoRadio) return null;
    return (
      <FormattedMessage {...messages.startArtistStationBasedOnArtist} values={{ artistName }} />
    );
  };

  const triggerStartArtistStationCoachmark = (): void => {
    logClickEvent(STATION_TRIGGER);
    openModal({
      modalType: 'ModalCoachMark',
      body: (
        <span>
          {' '}
          <FormattedMessage {...messages.albumStationsNotAvailable} />{' '}
          {getStartArtistStationMessage()}{' '}
        </span>
      ),
      buttons: getStartStationButton(),
    });
  };

  const onShareClick = (): $TSFixMe => {
    logClickEvent(SHARE);
    openShareModal(null, {
      shareParentSourceId: pandoraId,
      shareParentSourceType: D.PandoraTypes.ALBUM,
    });
  };

  return {
    addToMusicClick,
    onModalStartStationClick,
    onShareClick,
    pauseOnClick,
    playOnClick,
    removeMusicClick,
    triggerStartArtistStationCoachmark,
  } as const;
}

export const mapStateToProps: MapStateToProps<
  IAlbumButtonControlsStateProps,
  IAlbumButtonControlsOwnProps,
  $TSFixMe
> = (state: $TSFixMe) => {
  return {
    showPremiumAccess: D.Utils.Listener.showPremiumAccess(state),
    playbackMessages: D.getInDomain('playback.constants.PlaybackMessages', null),
  };
};

const mapDispatchToProps: MapDispatchToProps<
  IAlbumButtonControlsDispatchProps,
  IAlbumButtonControlsOwnProps
> = {
  addCollectionItem: D.addCollectionItem,
  closeModal: D.closeModal,
  openModal: D.openModal,
  openShareModal: D.openShareModal,
  pauseTrack: D.pauseTrack,
  playResumeSource: D.playResumeSource,
  removeCollectionItem: D.removeCollectionItem,
  startArtistStation: D.startStation,
};

export const withAlbumButtonControls = (
  WrappedComponent: React.ComponentType<$TSFixMe>,
): $TSFixMe => {
  const AlbumButtonControls: React.FC<IAlbumButtonControlsProps> = props => {
    const {
      playOnClick,
      pauseOnClick,
      removeMusicClick,
      addToMusicClick,
      triggerStartArtistStationCoachmark,
      onShareClick,
    } = useClickControls(props);

    return (
      <WrappedComponent
        playOnClick={playOnClick}
        pauseOnClick={pauseOnClick}
        removeMusicClick={removeMusicClick}
        addToMusicClick={addToMusicClick}
        triggerStartArtistStationCoachmark={triggerStartArtistStationCoachmark}
        onShareClick={onShareClick}
        {...props}
      />
    );
  };

  return connect(
    mapStateToProps,
    mapDispatchToProps,
    // @ts-ignore-error
  )(AlbumButtonControls);
};
